/* ==========================================================================
   Raptor Racing Smile helper, theme v6.8 / v2.13 item 5 (27 Sept 2026)
   Loaded with defer from layout/theme.liquid on every page.

   A tiny, dependency-free bridge to the Smile.io app embed so the Raptor
   Rewards page (sections/rr-rewards.liquid, assets/rr-rewards.js) and the
   announcement bar can read a member's Dino Dollars balance and open the
   Smile panel without each re-implementing the SDK dance.

   What the Smile embed gives us (probed live on the draft, 27 Sept 2026):
   - on load only a lite launcher exists and window.loadSmileSdk is a
     function; window.SmileUI and window.Smile are ABSENT until
     window.loadSmileSdk() is called (they appear 2 to 6 s later);
   - window.SmileUI then carries openPanel, closePanel, ready, on, ...;
   - window.Smile carries customer (current, login, logout),
     customerPointsWallet, customerVipStatus, earningRules, ...;
   - setting location.hash to '#smile-points-product' opens the panel.
   The exact shapes of Smile.customer.current and Smile.customerPointsWallet
   are NOT documented for this store (values, functions or promises), so
   every step below feature-detects and anything that is not a finite
   integer >= 0 is reported as unknown (null). Nothing here ever throws.
   1 Oct 2026: Smile now documents the SDK (dev.smile.io/js): wallet and
   customer are objects with async methods (customerPointsWallet.get(),
   customer.current()) and camelCase fields. trySdk() uses that first; the
   older feature-detecting path stays as the fallback.

   PUBLIC API: window.rrSmile
   --------------------------------------------------------------------------
   rrSmile.load(cb)
       Boots the Smile SDK once (guarded) and calls cb(true) when
       window.SmileUI exists (and SmileUI.ready has fired or timed out), or
       cb(false) after 10 s without it. Safe to call many times; every
       caller is queued and answered once.

   rrSmile.getBalance(cb)
       Calls cb(integer) with the signed-in customer's Dino Dollars balance,
       or cb(null) when it cannot be established (guest, Smile missing,
       unknown data shape, timeout). Tries, in order, only accepting a
       finite integer >= 0:
         (a) Smile.customerPointsWallet (value, function or promise), reading
             points_balance | balance | points from the result, result.wallet
             or result.customer_points_wallet;
         (b) Smile.customer.current (value, function or promise), reading
             points_balance from the customer object or customer.customer.
       The answer is cached for the page; total wall time is capped at 12 s.
       Example (announcement bar):
         window.rrSmile.getBalance(function (n) {
           if (n === null) return;              // unknown: say nothing
           bar.textContent = 'You have ' + n + ' Dino Dollars';
         });

   rrSmile.openPanel(deepLink)
       Opens the Smile panel on a deep link such as '#smile-points-product'
       or '#smile-points-activity-rules'. Uses SmileUI.openPanel when it is
       available (object form, then string form), otherwise sets
       location.hash to the deep link, which Smile also honours. Returns
       true when something was attempted.

   rrSmile.cachedBalance()
       The cached answer of getBalance: an integer, null (unknown) or
       undefined (not asked yet). Synchronous, for scripts that only want
       to react if a balance is already known.
   ========================================================================== */
(function () {
  'use strict';

  if (window.rrSmile) return;

  var LOAD_POLL_MS = 250;
  var LOAD_TIMEOUT_MS = 10000;
  var READY_FALLBACK_MS = 3000;
  var BALANCE_TIMEOUT_MS = 12000;

  var loadStarted = false;
  var loadSettled = false;
  var loadResult = false;
  var loadWaiters = [];

  var balanceCache; /* undefined = not asked, null = unknown, number = known */
  var balanceInFlight = false;
  var balanceWaiters = [];

  function safeCall(fn, arg) {
    try { fn(arg); } catch (e) { /* never let a caller break the helper */ }
  }

  function isCountingInt(v) {
    return typeof v === 'number' && isFinite(v) && Math.floor(v) === v && v >= 0;
  }

  /* Accept a finite integer >= 0, or a string made only of digits. */
  function toCountingInt(v) {
    if (typeof v === 'string' && /^\d{1,12}$/.test(v)) v = parseInt(v, 10);
    return isCountingInt(v) ? v : null;
  }

  /* ------------------------------------------------------------------------
     load
     ------------------------------------------------------------------------ */
  function settleLoad(ok) {
    if (loadSettled) return;
    loadSettled = true;
    loadResult = !!ok;
    var list = loadWaiters;
    loadWaiters = [];
    for (var i = 0; i < list.length; i++) safeCall(list[i], loadResult);
  }

  function awaitReady() {
    var ui = window.SmileUI;
    var done = false;
    var timer = null;
    function finish() {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      settleLoad(true);
    }
    try {
      if (ui && typeof ui.ready === 'function') {
        timer = setTimeout(finish, READY_FALLBACK_MS);
        var r = ui.ready(finish);
        if (r && typeof r.then === 'function') r.then(finish, finish);
        return;
      }
    } catch (e) { /* fall through */ }
    finish();
  }

  function load(cb) {
    if (typeof cb !== 'function') cb = function () {};
    try {
      if (window.SmileUI) {
        if (!loadSettled) { loadWaiters.push(cb); awaitReady(); return; }
        safeCall(cb, true);
        return;
      }
      if (loadSettled) { safeCall(cb, loadResult); return; }
      loadWaiters.push(cb);
      if (loadStarted) return;
      loadStarted = true;

      /* Smile's loader (window.loadSmileSdk) can itself arrive after this runs
         (1 Oct 2026: early callers on product pages), so ask as soon as it exists */
      var asked = false;
      function ask() {
        if (asked || typeof window.loadSmileSdk !== 'function') return;
        asked = true;
        try { window.loadSmileSdk(); } catch (e) { /* keep polling; Smile may still arrive on its own */ }
      }
      ask();

      var waited = 0;
      var poll = setInterval(function () {
        waited += LOAD_POLL_MS;
        ask();
        if (window.SmileUI) {
          clearInterval(poll);
          awaitReady();
        } else if (waited >= LOAD_TIMEOUT_MS) {
          clearInterval(poll);
          settleLoad(false);
        }
      }, LOAD_POLL_MS);
    } catch (e) {
      settleLoad(false);
    }
  }

  /* The documented SDK (window.Smile) lands a moment after SmileUI (seen 1 Oct
     2026: smile-ui-loaded, then smile-js-initialized, then smile-js-loaded), so
     callers that need its resources wait for it here: cb(true) once
     window.Smile.customer exists, cb(false) after 10 s. */
  function whenSdk(cb) {
    load(function (ok) {
      if (!ok) { safeCall(cb, false); return; }
      if (window.Smile && window.Smile.customer) { safeCall(cb, true); return; }
      var waited = 0;
      var poll = setInterval(function () {
        waited += LOAD_POLL_MS;
        if (window.Smile && window.Smile.customer) { clearInterval(poll); safeCall(cb, true); }
        else if (waited >= LOAD_TIMEOUT_MS) { clearInterval(poll); safeCall(cb, false); }
      }, LOAD_POLL_MS);
    });
  }

  /* ------------------------------------------------------------------------
     getBalance
     ------------------------------------------------------------------------ */

  /* Resolve a Smile member that may be a value, a function or a promise. */
  function resolveValue(owner, key, done) {
    var called = false;
    function once(v) {
      if (called) return;
      called = true;
      done(v);
    }
    try {
      if (!owner) { once(undefined); return; }
      var v = owner[key];
      if (typeof v === 'function') v = v.call(owner);
      if (v && typeof v.then === 'function') {
        v.then(function (r) { once(r); }, function () { once(undefined); });
        return;
      }
      once(v);
    } catch (e) {
      once(undefined);
    }
  }

  function readWallet(result) {
    var keys = ['pointsBalance', 'points_balance', 'balance', 'points'];
    var sources = [];
    if (result && typeof result === 'object') {
      sources.push(result);
      if (result.wallet && typeof result.wallet === 'object') sources.push(result.wallet);
      if (result.customer_points_wallet && typeof result.customer_points_wallet === 'object') sources.push(result.customer_points_wallet);
      if (result.data && typeof result.data === 'object') sources.push(result.data);
    } else {
      var direct = toCountingInt(result);
      if (direct !== null) return direct;
    }
    for (var i = 0; i < sources.length; i++) {
      for (var k = 0; k < keys.length; k++) {
        var n = toCountingInt(sources[i][keys[k]]);
        if (n !== null) return n;
      }
    }
    return null;
  }

  function readCustomer(result) {
    if (!result || typeof result !== 'object') return null;
    var sources = [result];
    if (result.customer && typeof result.customer === 'object') sources.push(result.customer);
    if (result.data && typeof result.data === 'object') sources.push(result.data);
    for (var i = 0; i < sources.length; i++) {
      var n = toCountingInt(sources[i].pointsBalance);
      if (n === null) n = toCountingInt(sources[i].points_balance);
      if (n !== null) return n;
    }
    return null;
  }

  function getBalance(cb) {
    if (typeof cb !== 'function') cb = function () {};
    if (balanceCache !== undefined) { safeCall(cb, balanceCache); return; }
    balanceWaiters.push(cb);
    if (balanceInFlight) return;
    balanceInFlight = true;

    var settled = false;
    var timer = setTimeout(function () { settle(null); }, BALANCE_TIMEOUT_MS);

    function settle(n) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      balanceCache = isCountingInt(n) ? n : null;
      balanceInFlight = false;
      var list = balanceWaiters;
      balanceWaiters = [];
      for (var i = 0; i < list.length; i++) safeCall(list[i], balanceCache);
    }

    function tryCustomer() {
      var smile = window.Smile;
      var customer = smile && smile.customer;
      if (!customer) { settle(null); return; }
      resolveValue(customer, 'current', function (result) {
        settle(readCustomer(result));
      });
    }

    /* The documented SDK (dev.smile.io/js, checked 1 Oct 2026): Smile.customer.current()
       resolves to null for guests, and Smile.customerPointsWallet.get() resolves to the
       member's wallet (it rejects for guests, so ask for the customer first). Resource
       fields are camelCase (pointsBalance). */
    function trySdk() {
      var smile = window.Smile;
      var wallet = smile && smile.customerPointsWallet;
      var customer = smile && smile.customer;
      if (!wallet || typeof wallet.get !== 'function' || !customer || typeof customer.current !== 'function') return false;
      /* Smile identifies a signed-in shopper a moment after it boots, so an early
         customer.current() can still be null (seen 1 Oct 2026 on the rewards page).
         Shopify's page data says whether someone is signed in: guests settle at once,
         signed-in shoppers are asked again every 500 ms until Smile knows them. */
      var shopifySignedIn = !!((window.__st && window.__st.cid) ||
        (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId));
      var tries = 0;
      function retry(fallback) {
        if (settled) return;
        if (shopifySignedIn && ++tries < 20) { setTimeout(ask, 500); return; }
        settle(fallback);  // guest, or Smile never recognised the shopper
      }
      function ask() {
        Promise.resolve().then(function () { return customer.current(); }).then(function (c) {
          if (settled) return;
          if (!c) { retry(null); return; }
          var fromCustomer = readCustomer(c);
          // the wallet can still refuse for a moment after the customer is known
          return wallet.get().then(function (w) {
            var n = readWallet(w);
            if (n !== null) settle(n); else retry(fromCustomer);
          }, function () { retry(fromCustomer); });
        }).catch(function () { retry(null); });
      }
      ask();
      return true;
    }

    function tryWallet() {
      if (trySdk()) return;
      var smile = window.Smile;
      if (!smile || !('customerPointsWallet' in Object(smile))) { tryCustomer(); return; }
      resolveValue(smile, 'customerPointsWallet', function (result) {
        var n = readWallet(result);
        if (n !== null) settle(n); else tryCustomer();
      });
    }

    try {
      load(function (ok) {
        if (settled) return;
        if (!ok) { settle(null); return; }
        whenSdk(function () { if (!settled) tryWallet(); });
      });
    } catch (e) {
      settle(null);
    }
  }

  /* ------------------------------------------------------------------------
     openPanel
     ------------------------------------------------------------------------ */
  function openPanel(deepLink) {
    var link = typeof deepLink === 'string' && deepLink ? deepLink : '#smile-home';
    var ui = window.SmileUI;
    if (ui && typeof ui.openPanel === 'function') {
      try {
        ui.openPanel({ deep_link: link });
        return true;
      } catch (e1) {
        try {
          ui.openPanel(link);
          return true;
        } catch (e2) { /* fall through to the hash */ }
      }
    }
    try {
      /* Smile honours the hash (verified live on the draft, 27 Sept 2026).
         Kick the SDK boot as well so the full panel can answer it. */
      load(function () {});
      if (window.location.hash === link) {
        window.location.hash = '';
      }
      window.location.hash = link;
      return true;
    } catch (e3) {
      return false;
    }
  }

  function cachedBalance() {
    return balanceCache;
  }

  /* ------------------------------------------------------------------
     Brand the Smile launcher and panel (spec v2.16 6E, 28 Sept 2026).
     Smile draws them in same-origin iframes coloured from its settings
     (Raptor Green with black text). A style tag injected into each Smile
     frame holds the launcher and the panel's primary elements at Raptor
     Green #6DBD4E with Asphalt text and icon (owner, 28 Sept). On phones
     (798px and under) the launcher becomes a 48px icon-only circle so it
     no longer covers Add to cart; the size of the host containers is in
     rr-brand.css 6e.1.
     ------------------------------------------------------------------ */
  var SMILE_CSS =
    /* owner, 28 Sept: Raptor Green #6DBD4E with Asphalt #231F20 text and icon (7.0:1) */
    '.launcher-button{--launcher-color:#6DBD4E!important}' +
    '.background-primary,.btn-primary,.btn.btn-primary,a.btn-primary{background-color:#6DBD4E!important;border-color:#6DBD4E!important;color:#231F20!important}' +
    '.smile-launcher-font-color-dark,.smile-launcher-font-color-light,.launcher-text{color:#231F20!important}' +
    'body>button{background-color:#6DBD4E!important;color:#231F20!important}' +
    'img.launcher-icon,body>button>img{filter:brightness(0) saturate(100%)}' +
    '.rr-phone .launcher-text,.rr-phone body>button>span{display:none!important}' +
    '.rr-phone .launcher-button,.rr-phone .launcher-container,.rr-phone body>button{width:48px!important;height:48px!important;min-width:48px!important;padding:0!important;display:flex!important;align-items:center!important;justify-content:center!important;border-radius:50%!important}' +
    '.rr-phone .launcher-content-container{padding:0!important;margin:0!important;justify-content:center!important}' +
    '.rr-phone img.launcher-icon,.rr-phone body>button>img{margin:0!important}';

  function isPhone() {
    try { return window.matchMedia('(max-width: 798px)').matches; } catch (e) { return false; }
  }

  function brandFrame(frame) {
    var d;
    try { d = frame.contentDocument; } catch (e) { return; }
    if (!d || !d.documentElement) return;
    d.documentElement.classList.toggle('rr-phone', isPhone());
    if (d.getElementById('rr-smile-brand')) return;
    var head = d.head || d.documentElement;
    var s = d.createElement('style');
    s.id = 'rr-smile-brand';
    s.textContent = SMILE_CSS;
    head.appendChild(s);
  }

  function brandAll() {
    var frames = document.querySelectorAll('#smile-ui-lite-launcher-frame-container iframe, .smile-launcher-frame-container iframe, .smile-panel-frame-container iframe, #smile-ui-container iframe');
    for (var i = 0; i < frames.length; i++) {
      brandFrame(frames[i]);
      if (!frames[i].rrBrandHooked) {
        frames[i].rrBrandHooked = true;
        frames[i].addEventListener('load', (function (f) { return function () { brandFrame(f); }; })(frames[i]));
      }
    }
  }

  function watchSmile() {
    brandAll();
    if (!('MutationObserver' in window) || !document.body) return;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.setTimeout(function () { queued = false; brandAll(); }, 60);
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', brandAll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchSmile);
  } else {
    watchSmile();
  }

  /* ------------------------------------------------------------------
     Bonus Dino Dollars lines (owner, 1 Oct 2026)
     Elements marked data-rr-bonus="review" or "clearance" start hidden and
     are filled from Smile's own earning rules ("Write a Product Review",
     "Purchase a Clearance Item"), so the figure always matches what Smile
     awards; if the rule can't be read the line stays hidden. Markup:
       <span data-rr-bonus="clearance" data-pre=", plus " data-b="{n} bonus
       Dino Dollars" data-post=" for your clearance item" hidden></span>
     The rules come from Smile.earningRules.get() (the SDK only boots when
     such a line comes within 400px of the screen) and are kept in
     localStorage for 12 hours, so most pages never boot it for this.
     ------------------------------------------------------------------ */
  var RULES_KEY = 'rr-smile-rules-v1';
  var RULES_TTL = 12 * 3600 * 1000;
  var BONUS_RE = { review: /review/i, clearance: /clearance/i };
  var rulesCache;
  var rulesWaiters = [];
  var rulesInFlight = false;

  function earningRules(cb) {
    if (rulesCache !== undefined) { safeCall(cb, rulesCache); return; }
    try {
      var saved = JSON.parse(window.localStorage.getItem(RULES_KEY) || 'null');
      if (saved && saved.t > Date.now() - RULES_TTL && saved.r && saved.r.length) {
        rulesCache = saved.r; safeCall(cb, rulesCache); return;
      }
    } catch (e) { /* storage blocked: ask Smile */ }
    rulesWaiters.push(cb);
    if (rulesInFlight) return;
    rulesInFlight = true;
    function done(r) {
      rulesCache = r;
      rulesInFlight = false;
      if (r) { try { window.localStorage.setItem(RULES_KEY, JSON.stringify({ t: Date.now(), r: r })); } catch (e) {} }
      var list = rulesWaiters; rulesWaiters = [];
      for (var i = 0; i < list.length; i++) safeCall(list[i], r);
    }
    whenSdk(function (ok) {
      var api = ok && window.Smile && window.Smile.earningRules;
      if (!api || typeof api.get !== 'function') { done(null); return; }
      /* like the wallet, this can refuse for a moment while Smile signs a
         shopper in (1 Oct 2026), so try again for up to 10 s */
      var tries = 0;
      function ask() {
        Promise.resolve().then(function () { return api.get(); }).then(function (rules) {
          var out = [];
          (rules || []).forEach(function (r) {
            var v = r && r.rewardValue && r.rewardValue.type === 'fixed' && r.rewardValue.fixed ? toCountingInt(r.rewardValue.fixed.value) : null;
            if (r && r.name && v) out.push({ n: String(r.name), v: v });
          });
          if (out.length) done(out); else again();
        }).catch(again);
      }
      function again() {
        if (++tries < 20) setTimeout(ask, 500); else done(null);
      }
      ask();
    });
  }

  function bonus(kind, cb) {
    var re = BONUS_RE[kind];
    if (!re) { safeCall(cb, null); return; }
    earningRules(function (rules) {
      var hit = null;
      (rules || []).some(function (r) { if (re.test(r.n)) { hit = r.v; return true; } return false; });
      safeCall(cb, hit);
    });
  }

  function fillBonus(el) {
    bonus(el.getAttribute('data-rr-bonus'), function (n) {
      if (!n) return;
      var label = (el.getAttribute('data-b') || '{n} Dino Dollars').replace('{n}', n.toLocaleString('en-CA'));
      while (el.firstChild) el.removeChild(el.firstChild);
      el.appendChild(document.createTextNode(el.getAttribute('data-pre') || ''));
      var b = document.createElement('b'); b.textContent = label; el.appendChild(b);
      el.appendChild(document.createTextNode(el.getAttribute('data-post') || ''));
      el.hidden = false;
    });
  }

  var bonusIO = null;
  function scanBonuses() {
    var els = document.querySelectorAll('[data-rr-bonus]:not([data-rr-seen])');
    if (!els.length) return;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      el.setAttribute('data-rr-seen', '1');
      if (rulesCache !== undefined || !('IntersectionObserver' in window)) { fillBonus(el); continue; }
      if (!bonusIO) {
        bonusIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            bonusIO.unobserve(en.target);
            var list = en.target.__rrBonus || [];
            en.target.__rrBonus = null;
            list.forEach(fillBonus);
          });
        }, { rootMargin: '400px 0px' });
      }
      // a hidden line has no box, so watch its parent
      var target = el.parentElement || el;
      target.__rrBonus = (target.__rrBonus || []).concat(el);
      bonusIO.observe(target);
    }
  }

  function watchBonuses() {
    scanBonuses();
    if (!window.MutationObserver || !document.body) return;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.setTimeout(function () { queued = false; scanBonuses(); }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBonuses);
  } else {
    watchBonuses();
  }

  window.rrSmile = {
    load: load,
    getBalance: getBalance,
    openPanel: openPanel,
    cachedBalance: cachedBalance,
    bonus: bonus
  };
})();
